import React, { useState, useRef, useEffect } from 'react';

const Navbar = ({ user, onLogout, onSignIn, onNavigate }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch avatar on mount and whenever user changes
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_API_URL}/api/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          setAvatarUrl(d.user?.profile_picture_url || null);
          setStats(d.stats);
        })
        .catch(() => {});
    } else {
      setAvatarUrl(null);
      setStats(null);
    }
  }, [user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (dropdownOpen && user && !stats) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_API_URL}/api/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setStats(d.stats))
        .catch(() => {});
    }
  }, [dropdownOpen, user]);

  // Close mobile menu on navigation
  const navigate = (path) => {
    onNavigate(path);
    setMobileMenuOpen(false);
  };

  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : '?';
  const accountType = user?.is_admin ? 'Admin' : 'Member';
  const displayName = user?.username ? user.username.charAt(0).toUpperCase() + user.username.slice(1) : '';

  const styles = {
    nav: {
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: isMobile ? '18px 24px' : '24px 48px',
      borderBottom: '1px solid #2a2a2a',
      backgroundColor: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(10px)',
    },
    logo: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', fontWeight: 300, color: '#c9a96e', letterSpacing: '0.1em', cursor: 'pointer' },
    links: { display: 'flex', gap: '40px', alignItems: 'center' },
    link: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 400, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a09890', cursor: 'pointer', transition: 'color 0.3s ease' },
    button: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 400, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c9a96e', background: 'none', border: '1px solid #c9a96e', padding: '10px 24px', cursor: 'pointer' },
    profileBtn: { width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(201, 169, 110, 0.1)', border: '1px solid #c9a96e', color: '#c9a96e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontFamily: 'Cormorant Garamond, serif', fontWeight: 500, flexShrink: 0, overflow: 'hidden', padding: 0 },
    dropdownWrapper: { position: 'relative' },
    dropdown: { position: 'absolute', top: 'calc(100% + 12px)', right: 0, width: '240px', background: '#111111', border: '1px solid #2a2a2a', zIndex: 200, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' },
    dropdownHeader: { padding: '20px 20px 16px', borderBottom: '1px solid #1e1e1e' },
    dropdownUsername: { fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', fontWeight: 400, color: '#f5f0eb', marginBottom: '4px', letterSpacing: '0.12em' },
    dropdownEmail: { fontSize: '0.7rem', color: '#6b6460', letterSpacing: '0.05em', marginBottom: '8px' },
    accountBadge: { display: 'inline-block', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#c9a96e', border: '1px solid rgba(201,169,110,0.3)', padding: '3px 8px' },
    dropdownStats: { padding: '14px 20px', borderBottom: '1px solid #1e1e1e', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    dropdownStat: { textAlign: 'center' },
    dropdownStatVal: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#c9a96e', lineHeight: 1, marginBottom: '3px' },
    dropdownStatLabel: { fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6460' },
    dropdownMenu: { padding: '8px 0' },
    dropdownItem: { display: 'block', width: '100%', padding: '11px 20px', fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a09890', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' },
    dropdownItemDanger: { display: 'block', width: '100%', padding: '11px 20px', fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8a6060', background: 'none', border: 'none', borderTop: '1px solid #1e1e1e', textAlign: 'left', cursor: 'pointer', marginTop: '4px' },
    // Hamburger
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', flexDirection: 'column', gap: '5px', zIndex: 101 },
    hamburgerLine: { width: '22px', height: '1px', background: '#c9a96e', transition: 'all 0.3s ease', display: 'block' },
    // Mobile drawer overlay
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 98, backdropFilter: 'blur(4px)' },
    // Mobile drawer
    drawer: {
      position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px',
      background: '#0f0f0f', borderLeft: '1px solid #2a2a2a',
      zIndex: 99, display: 'flex', flexDirection: 'column',
      transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease',
      paddingTop: '80px',
    },
    drawerUser: { padding: '24px', borderBottom: '1px solid #1e1e1e' },
    drawerAvatar: { width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201,169,110,0.1)', border: '1px solid #c9a96e', color: '#c9a96e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontWeight: 500, marginBottom: '12px' },
    drawerUsername: { fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', letterSpacing: '0.12em', color: '#f5f0eb', marginBottom: '3px' },
    drawerEmail: { fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', color: '#4a4a4a' },
    drawerNav: { flex: 1, padding: '16px 0' },
    drawerLink: { display: 'block', width: '100%', padding: '16px 28px', fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a09890', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid #161616' },
    drawerFooter: { padding: '16px 0', borderTop: '1px solid #1e1e1e' },
    drawerDanger: { display: 'block', width: '100%', padding: '16px 28px', fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8a6060', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' },
  };

  // ── Mobile layout ──
  if (isMobile) {
    return (
      <>
        <nav style={styles.nav}>
          <div style={styles.logo} onClick={() => navigate('/')}>Flakon</div>
          <button
            style={styles.hamburger}
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Menu"
          >
            <span style={{
              ...styles.hamburgerLine,
              transform: mobileMenuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none',
            }} />
            <span style={{
              ...styles.hamburgerLine,
              opacity: mobileMenuOpen ? 0 : 1,
            }} />
            <span style={{
              ...styles.hamburgerLine,
              transform: mobileMenuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none',
            }} />
          </button>
        </nav>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div style={styles.overlay} onClick={() => setMobileMenuOpen(false)} />
        )}

        {/* Drawer */}
        <div style={styles.drawer}>
          {user ? (
            <>
              <div style={styles.drawerUser}>
              <div style={{ ...styles.drawerAvatar, overflow: 'hidden', padding: 0 }}>
                {avatarUrl
                  ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : initials}
              </div>
                <p style={styles.drawerUsername}>{displayName}</p>
                <p style={styles.drawerEmail}>{user.email}</p>
              </div>
              <div style={styles.drawerNav}>
                <button style={styles.drawerLink} onClick={() => navigate('/library')}>Library</button>
                <button style={styles.drawerLink} onClick={() => navigate('/wishlist')}>Wishlist</button>
                <button style={styles.drawerLink} onClick={() => navigate('/discover')}>Discover</button>
                <button style={styles.drawerLink} onClick={() => navigate('/feed')}>Feed</button>
                <button style={styles.drawerLink} onClick={() => navigate('/journal')}>Journal</button>
                <button style={styles.drawerLink} onClick={() => navigate('/profile')}>Edit Profile</button>
                <button style={styles.drawerLink} onClick={() => navigate(`/u/${user.username}`)}>View Public Profile</button>
                {user.is_admin && <button style={{ ...styles.drawerLink, color: '#c9a96e' }} onClick={() => navigate('/admin')}>Admin Panel</button>}
              </div>
              <div style={styles.drawerFooter}>
                <button style={styles.drawerDanger} onClick={() => { onLogout(); setMobileMenuOpen(false); }}>Sign Out</button>
              </div>
            </>
          ) : (
            <div style={styles.drawerNav}>
              <button style={styles.drawerLink} onClick={() => navigate('/discover')}>Discover</button>
              <button style={{ ...styles.drawerLink, color: '#c9a96e' }} onClick={() => { onSignIn(); setMobileMenuOpen(false); }}>Sign In</button>
            </div>
          )}
        </div>
      </>
    );
  }

  // ── Desktop layout ──
  return (
    <nav style={styles.nav}>
      <div style={styles.logo} onClick={() => onNavigate('/')}>Flakon</div>
      <div style={styles.links}>
        {user ? (
          <>
            <span style={styles.link} onClick={() => onNavigate('library')}>Library</span>
            <span style={styles.link} onClick={() => onNavigate('/wishlist')}>Wishlist</span>
            <span style={styles.link} onClick={() => onNavigate('/discover')}>Discover</span>
            <span style={styles.link} onClick={() => onNavigate('/feed')}>Feed</span>
            <span style={styles.link} onClick={() => onNavigate('/journal')}>Journal</span>
            <div style={styles.dropdownWrapper} ref={dropdownRef}>
              <div style={styles.profileBtn} onClick={() => setDropdownOpen(o => !o)}>
                {avatarUrl
                  ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : initials}
              </div>
              {dropdownOpen && (
                <div style={styles.dropdown}>
                  <div style={styles.dropdownHeader}>
                    <div style={styles.dropdownUsername}>{displayName}</div>
                    <span style={styles.accountBadge}>{accountType}</span>
                  </div>
                  <div style={styles.dropdownStats}>
                    <div style={styles.dropdownStat}>
                      <div style={styles.dropdownStatVal}>{stats?.total_fragrances || '—'}</div>
                      <div style={styles.dropdownStatLabel}>In Library</div>
                    </div>
                    <div style={styles.dropdownStat}>
                      <div style={styles.dropdownStatVal}>£{stats?.total_value ? Math.round(stats.total_value) : '—'}</div>
                      <div style={styles.dropdownStatLabel}>Est. Value</div>
                    </div>
                  </div>
                  <div style={styles.dropdownMenu}>
                    {user.is_admin && (
                      <button style={{ ...styles.dropdownItem, color: '#c9a96e' }} onClick={() => { onNavigate('/admin'); setDropdownOpen(false); }}>Admin Panel</button>
                    )}
                    <button style={styles.dropdownItem} onClick={() => { onNavigate('/profile'); setDropdownOpen(false); }}>Edit Profile</button>
                    <button style={styles.dropdownItem} onClick={() => { onNavigate(`/u/${user.username}`); setDropdownOpen(false); }}>View Public Profile</button>
                    <button style={styles.dropdownItemDanger} onClick={() => { onLogout(); setDropdownOpen(false); }}>Sign Out</button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <span style={styles.link}>Discover</span>
            <button style={styles.button} onClick={onSignIn}>Sign In</button>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;